import type { CertificationBody } from "../api/client";
import { Callout } from "../components/Callout";

/**
 * Screen 08 · Application submitted.
 *
 * What was submitted, read back from the server rather than echoed from what the browser
 * sent. The distinction matters on exactly this screen: the applicant is being shown what
 * a Complao administrator will assess, and showing them the payload instead would show
 * them what they *tried* to submit.
 *
 * There is no "start a new application" button, though the prototype has one. A
 * certification body has one application, and the prototype's button exists so a reviewer
 * can walk the flow twice.
 */
export function ApplicationSubmitted({ application }: { application: CertificationBody }) {
  const body = application.accreditation_body ?? "your accreditation body";

  return (
    <>
      <div className="eyebrow">Certification body onboarding</div>
      <h1>Application submitted</h1>

      <Callout tone="info" title="Submitted for verification by Complao.">
        Your accreditation certificate and insurance evidence are being checked against{" "}
        {body}&rsquo;s public register. You cannot configure audits until verification
        completes.
      </Callout>

      <section className="card">
        <h2>What was submitted</h2>
        <dl className="kv">
          <dt>Legal entity</dt>
          <dd>{application.legal_name}</dd>

          <dt>Accreditation</dt>
          <dd>
            {body} {application.accreditation_number}
            {application.accreditation_expiry ? ` — expires ${application.accreditation_expiry}` : null}
          </dd>

          <dt>Accredited scopes</dt>
          <dd>
            {(application.scopes ?? []).map((scope, index) => {
              const count = (scope.sectors ?? []).length;
              return (
                <span key={scope.standard}>
                  {index > 0 ? " · " : null}
                  {scope.standard}{" "}
                  <span className="sub">
                    ({count} {count === 1 ? "sector" : "sectors"})
                  </span>
                </span>
              );
            })}
          </dd>

          <dt>Standards requested</dt>
          <dd>{(application.enabled_standards ?? []).join(" · ")}</dd>

          <dt>Evidence</dt>
          <dd>
            {(application.evidence ?? []).length === 0 ? (
              <span className="sub">None attached</span>
            ) : (
              (application.evidence ?? []).map((entry) => (
                <span className="mono" key={entry.id} style={{ display: "block" }}>
                  {entry.filename}
                </span>
              ))
            )}
          </dd>

          <dt>Audit reference prefix</dt>
          <dd className="mono">{application.reference_prefix}-2026-0001</dd>

          <dt>Duration threshold</dt>
          <dd>
            {application.duration_threshold}% <span className="sub">of calculated audit time</span>
          </dd>

          <dt>Stage 1 share</dt>
          <dd>
            {application.stage1_percentage}%{" "}
            <span className="sub">certification body procedure</span>
          </dd>

          <dt>Signed by</dt>
          <dd>
            {application.signed_by}
            {application.signed_at ? ` · ${formatMoment(application.signed_at)}` : null}
          </dd>
        </dl>
      </section>

      <Callout tone="warn" title="Verification is not activation.">
        Whether you may sign in is a separate decision an administrator has already taken.
        Verification is the Cl. 5.2 assessment of the accreditation itself, and it is what
        this screen is waiting on.
      </Callout>
    </>
  );
}

/** `2026-07-28 11:36`, as screen 08 renders it. */
function formatMoment(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} ` +
    `${pad(when.getHours())}:${pad(when.getMinutes())}`
  );
}
